import { PropsWithChildren } from 'react';

import { useField } from 'react-final-form';

import { ConsolidationMethod } from './ConsolidationMethod';
import { Section } from './Section';
import {
  FIELD_NAMES,
  AVAILABLE_KENT_CONTINUOUS_REBALANCING_CONFIG_MODES,
} from '../_constants';
import { FormValues } from '../_types';

import { useRffFormState } from '@cast/shared-rff-ui';

import { getDocsLink } from '@cast/utils';

import { ExternalLink, Typography, Box, Stack } from '@cast/design-system';

const Layout = ({ children }: PropsWithChildren) => {
  return (
    <Section
      title="Consolidation method"
      action={
        <ExternalLink
          link={getDocsLink('docs_evictor')}
          sx={{ typography: 'P12M' }}
        >
          Learn more
        </ExternalLink>
      }
    >
      <Box
        display="grid"
        gridTemplateColumns="repeat(3, minmax(364px, 1fr))"
        minHeight="186px"
        gap={16}
      >
        {children}
      </Box>
    </Section>
  );
};

const OutdatedKentrollerTooltipContent = () => {
  return (
    <Stack gap={8} width={224}>
      <Typography variant="P12R">
        Current Kentroller version doesn&apos;t support continuous rebalancing.
        Upgrade to enable new features.
      </Typography>

      <ExternalLink
        link={getDocsLink('docs_kent_kentroller')}
        sx={{ typography: 'P12M' }}
      >
        Documentation
      </ExternalLink>
    </Stack>
  );
};

type Props = {
  hasOutdatedKentroller: boolean;
};

export const ConsolidationMethodSelection = ({
  hasOutdatedKentroller,
}: Props) => {
  const { mode: formMode } = useRffFormState();
  const readOnly = formMode === 'view';
  const field = useField<FormValues['mode']>(FIELD_NAMES.mode);
  const enabledField = useField<FormValues['enabled']>(FIELD_NAMES.enabled);

  return (
    <Layout>
      {AVAILABLE_KENT_CONTINUOUS_REBALANCING_CONFIG_MODES.map((mode) => {
        const isModeIncompatibleWithKentroller =
          mode === 'CONTINUOUS_REBALANCING_CYCLE' && hasOutdatedKentroller;

        return (
          <ConsolidationMethod
            disabled={
              readOnly ||
              !enabledField.input.value ||
              isModeIncompatibleWithKentroller
            }
            key={mode}
            mode={mode}
            selected={field.input.value === mode}
            onSelect={() => field.input.onChange(mode)}
            tooltipContent={
              isModeIncompatibleWithKentroller ? (
                <OutdatedKentrollerTooltipContent />
              ) : undefined
            }
          />
        );
      })}
    </Layout>
  );
};

const ConsolidationMethodSelectionSkeleton = () => {
  return (
    <Layout>
      {AVAILABLE_KENT_CONTINUOUS_REBALANCING_CONFIG_MODES.map((mode) => (
        <ConsolidationMethod.Skeleton key={mode} />
      ))}
    </Layout>
  );
};

ConsolidationMethodSelection.Skeleton = ConsolidationMethodSelectionSkeleton;
